import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'

export const CREATORS_QUERY_KEYS = {
  all: ['creators'] as const,
  list: (params: Record<string, unknown>) => ['creators', 'list', params] as const,
  detail: (id: string) => ['creators', 'detail', id] as const,
  dashboard: (params: Record<string, unknown>) => ['creators', 'dashboard', params] as const,
  commissionTiers: ['commission-tiers'] as const,
  creatorPrograms: ['creator-programs'] as const,
  niches: ['creator-niches'] as const,
}

export function useCreatorDashboard(filters?: {
  startDate?: string
  endDate?: string
  programId?: string
  tierId?: string
}) {
  return useQuery({
    queryKey: CREATORS_QUERY_KEYS.dashboard(filters ?? {}),
    queryFn: () => api.creators.getDashboard(filters),
  })
}

/**
 * Fetches a paginated list of creators with optional filters and sorting.
 *
 * @param page - Page number to fetch (defaults to 1)
 * @param limit - Number of items per page (defaults to 20)
 * @param filters - Optional filtering options:
 *   - `search`: text to search creators by
 *   - `status`: creator status to filter by
 *   - `tierId`: commission tier id to filter by
 * @param sort - Optional sorting options:
 *   - `sortBy`: field to sort on
 *   - `sortDirection`: 'asc' or 'desc'
 * @returns The React Query result for the creators list query
 */
export function useCreators(
  page = 1,
  limit = 20,
  filters?: {
    search?: string
    status?: string
    tierId?: string
    niche?: string
    type?: string
    programId?: string
  },
  sort?: { sortBy?: string | null; sortDirection?: 'asc' | 'desc' | null }
) {
  const params = { page, limit, ...filters, ...sort }

  return useQuery({
    queryKey: CREATORS_QUERY_KEYS.list(params),
    queryFn: () =>
      api.creators.list(
        page,
        limit,
        filters?.search,
        filters?.status,
        filters?.tierId,
        sort?.sortBy ?? undefined,
        sort?.sortDirection ?? undefined,
        filters?.niche,
        filters?.type,
        filters?.programId
      ),
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Fetches a creator's details and exposes the react-query result for that creator.
 *
 * @param id - Creator identifier; when falsy the query is disabled
 * @returns The query result containing the creator's details
 */
export function useCreator(id: string) {
  return useQuery({
    queryKey: CREATORS_QUERY_KEYS.detail(id),
    queryFn: () => api.creators.get(id),
    enabled: !!id,
  })
}

export function useUpdateCreator() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof api.creators.update>[1] }) =>
      api.creators.update(id, data),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(CREATORS_QUERY_KEYS.detail(id), data)
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })
      toast.success('Creator atualizado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao atualizar creator', { description: error.message })
    },
  })
}

export function useDeleteCreator() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.creators.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })
      toast.success('Creator excluído com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao excluir creator', { description: error.message })
    },
  })
}

export function useEnrichCreatorInstagram() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.creators.enrichInstagram(id),
    onSuccess: (data, id) => {
      queryClient.setQueryData(CREATORS_QUERY_KEYS.detail(id), data)
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })
    },
  })
}

/**
 * Fetches and caches the list of commission tiers.
 *
 * @returns The React Query result containing commission tiers data and query metadata
 */
export function useCommissionTiers() {
  return useQuery({
    queryKey: CREATORS_QUERY_KEYS.commissionTiers,
    queryFn: () => api.commissionTiers.list(),
  })
}

export function useCreatorNiches() {
  return useQuery({
    queryKey: CREATORS_QUERY_KEYS.niches,
    queryFn: () => api.creators.listNiches(),
  })
}

/**
 * Fetches the sales summary for a specific creator.
 *
 * @param creatorId - The ID of the creator whose sales summary to fetch
 * @returns The sales summary data for the specified creator
 */
export function useCreatorSalesSummary(creatorId: string) {
  return useQuery({
    queryKey: ['creators', creatorId, 'sales', 'summary'],
    queryFn: () => api.creators.getSalesSummary(creatorId),
    enabled: !!creatorId,
  })
}

/**
 * Provides a cached query for a creator's sales list.
 *
 * The query is disabled when `creatorId` is falsy.
 *
 * @param creatorId - The identifier of the creator whose sales are being fetched
 * @param params - Optional pagination parameters for the sales list (`page`, `limit`)
 * @returns The react-query result containing the creator's sales data and query metadata
 */
export function useCreatorSales(creatorId: string, params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['creators', creatorId, 'sales', params],
    queryFn: () => api.creators.listSales(creatorId, params),
    enabled: !!creatorId,
  })
}

/**
 * Fetches and caches a paginated list of creator payouts filtered by the provided parameters.
 *
 * @param params - Optional filters and pagination for the payouts list.
 *   - page: The page number to fetch (1-based).
 *   - limit: Number of items per page.
 *   - creatorId: Filter payouts to a specific creator by ID.
 *   - status: Filter payouts by status.
 * @returns The query result containing the paginated creator payouts data and status metadata.
 */
export function useCreatorPayouts(params?: {
  page?: number
  limit?: number
  creatorId?: string
  status?: string
}) {
  return useQuery({
    queryKey: ['creator-payouts', params],
    queryFn: () => api.creatorPayouts.list(params),
  })
}

/**
 * Fetches payout details for a specific creator payout.
 *
 * @param id - The payout identifier to fetch
 * @returns The query result containing the creator payout details for the given `id`
 */
export function useCreatorPayoutDetails(id: string) {
  return useQuery({
    queryKey: ['creator-payouts', id],
    queryFn: () => api.creatorPayouts.getById(id),
    enabled: !!id,
  })
}

/**
 * Fetches posts for a given creator.
 *
 * The underlying query is disabled when `creatorId` is falsy.
 *
 * @param params - Optional query parameters: `page` and `limit` control pagination; `platform` and `type` filter the results.
 * @returns The react-query result for the creator's posts list, including fetched `data`, `status`, and query helpers.
 */
export function useCreatorPosts(
  creatorId: string,
  params?: { page?: number; limit?: number; platform?: string; type?: string }
) {
  return useQuery({
    queryKey: ['creators', creatorId, 'posts', params],
    queryFn: () => api.creatorPosts.list(creatorId, params),
    enabled: !!creatorId,
  })
}

/**
 * Fetches post statistics for a specific creator.
 *
 * @param creatorId - The creator's identifier; when falsy the query is disabled
 * @returns The React Query result containing the creator's post statistics
 */
export function useCreatorPostStats(creatorId: string) {
  return useQuery({
    queryKey: ['creators', creatorId, 'posts', 'stats'],
    queryFn: () => api.creatorPosts.getStats(creatorId),
    enabled: !!creatorId,
  })
}

/**
 * Fetches a creator's seedings list and associated query state.
 *
 * @param creatorId - The creator's unique identifier; the query is disabled if falsy
 * @param params - Optional pagination and filter options: `page`, `limit`, and `status`
 * @returns The React Query result containing the seedings list and query metadata
 */
export function useCreatorSeedings(
  creatorId: string,
  params?: { page?: number; limit?: number; status?: string }
) {
  return useQuery({
    queryKey: ['creators', creatorId, 'seedings', params],
    queryFn: () => api.seedings.list(creatorId, params),
    enabled: !!creatorId,
  })
}

/**
 * Fetches details for a single seeding by its identifier.
 *
 * The query is disabled when `id` is falsy.
 *
 * @param id - The seeding identifier to fetch
 * @returns The React Query result containing the seeding details data and query state
 */
export function useCreatorSeedingDetails(id: string) {
  return useQuery({
    queryKey: ['seedings', id],
    queryFn: () => api.seedings.getById(id),
    enabled: !!id,
  })
}

export function useDeleteSeeding() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ creatorId, seedingId }: { creatorId: string; seedingId: string }) =>
      api.seedings.delete(creatorId, seedingId),
    onSuccess: (_, { creatorId }) => {
      queryClient.invalidateQueries({ queryKey: ['creators', creatorId, 'seedings'] })
      toast.success('Envio removido!')
    },
    onError: (error) => {
      toast.error('Erro ao remover envio', { description: error.message })
    },
  })
}

// ─── Bulk Operations ────────────────────────────────────────────────────

export function useBulkSeeding() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { creatorIds: string[]; products: unknown[]; notes?: string }) =>
      api.creators.bulkSeeding(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })
      const errors = result.data.filter((r: { error: string | null }) => r.error)
      if (errors.length === 0) {
        toast.success(`Seeding enviado para ${result.data.length} creators!`)
      } else {
        toast.warning(`Seeding enviado com ${errors.length} erro(s) de ${result.data.length} total`)
      }
    },
    onError: (error) => {
      toast.error('Erro no seeding em lote', { description: error.message })
    },
  })
}

export function useBulkUpdateCreatorStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ creatorIds, status }: { creatorIds: string[]; status: string }) => {
      const results = await Promise.allSettled(
        creatorIds.map((id) =>
          api.creators.update(id, { status } as unknown as Parameters<
            typeof api.creators.update
          >[1])
        )
      )
      return results
    },
    onSuccess: (_, { creatorIds, status }) => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })
      toast.success(`${creatorIds.length} creators atualizados para "${status}"`)
    },
    onError: (error) => {
      toast.error('Erro ao atualizar status em lote', { description: error.message })
    },
  })
}

// ─── Creator Notes ──────────────────────────────────────────────────────

export function useCreatorNotes(creatorId: string) {
  return useQuery({
    queryKey: ['creators', creatorId, 'notes'],
    queryFn: () => api.creatorNotes.list(creatorId),
    enabled: !!creatorId,
  })
}

export function useAddCreatorNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ creatorId, text }: { creatorId: string; text: string }) =>
      api.creatorNotes.create(creatorId, text),
    onSuccess: (_, { creatorId }) => {
      queryClient.invalidateQueries({ queryKey: ['creators', creatorId, 'notes'] })
      toast.success('Nota adicionada!')
    },
    onError: (error) => {
      toast.error('Erro ao adicionar nota', { description: error.message })
    },
  })
}

export function useDeleteCreatorNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ creatorId, noteId }: { creatorId: string; noteId: string }) =>
      api.creatorNotes.delete(creatorId, noteId),
    onSuccess: (_, { creatorId }) => {
      queryClient.invalidateQueries({ queryKey: ['creators', creatorId, 'notes'] })
      toast.success('Nota removida')
    },
    onError: (error) => {
      toast.error('Erro ao remover nota', { description: error.message })
    },
  })
}

// ─── Creator Programs ──────────────────────────────────────────────────

export function useCreatorPrograms() {
  return useQuery({
    queryKey: CREATORS_QUERY_KEYS.creatorPrograms,
    queryFn: () => api.creatorPrograms.list(),
  })
}

export function useCreateCreatorProgram() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof api.creatorPrograms.create>[0]) =>
      api.creatorPrograms.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.creatorPrograms })
      toast.success('Programa criado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao criar programa', { description: error.message })
    },
  })
}

export function useUpdateCreatorProgram() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof api.creatorPrograms.update>[1]
    }) => api.creatorPrograms.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.creatorPrograms })
      toast.success('Programa atualizado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao atualizar programa', { description: error.message })
    },
  })
}

export function useForceSyncCreatorProgram() {
  return useMutation({
    mutationFn: (id: string) => api.creatorPrograms.forceSync(id),
    onSuccess: () => {
      toast.success('Sincronização enfileirada!')
    },
    onError: (error) => {
      toast.error('Erro ao sincronizar', { description: error.message })
    },
  })
}

// ─── Creator Coupon Operations ──────────────────────────────────────────

export function useSyncCreatorCoupon() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (creatorId: string) => api.creators.syncCoupon(creatorId),
    onSuccess: (result, creatorId) => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.detail(creatorId) })
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })
      if (!result.configDiff?.length) {
        toast.success('Cupom sincronizado! Configurações já alinhadas.')
      }
      // When configDiff exists, the component handles showing the details
    },
    onError: (error) => {
      toast.error('Erro ao sincronizar cupom', { description: error.message })
    },
  })
}

export function useLinkCreatorCoupon() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ creatorId, syncToProgram }: { creatorId: string; syncToProgram?: boolean }) =>
      api.creators.linkCoupon(creatorId, syncToProgram),
    onSuccess: (result, { creatorId }) => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.detail(creatorId) })
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })

      // Show warnings as toast if any
      if (result.warnings?.length) {
        toast.warning('Cupom vinculado com avisos', {
          description: result.warnings.join('. '),
        })
      } else if (!result.configDiff?.length) {
        // Only auto-toast success when there's no diff to show
        toast.success('Cupom vinculado! Configurações alinhadas com o programa.')
      }
      // When configDiff exists, the component handles showing the modal
    },
    onError: (error) => {
      toast.error('Erro ao vincular cupom', { description: error.message })
    },
  })
}

export function useDeactivateCreatorCoupon() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (creatorId: string) => api.creators.deactivateCoupon(creatorId),
    onSuccess: (_, creatorId) => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.detail(creatorId) })
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })
      toast.success('Cupom desativado!')
    },
    onError: (error) => {
      toast.error('Erro ao desativar cupom', { description: error.message })
    },
  })
}
